import { useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTrigger,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  SearchField,
} from 'react-aria-components'
import type { Selection } from 'react-aria-components'

/**
 * Was the user's LAST interaction a key press? Drives auto-open-on-focus:
 * tabbing (or `/`) onto a picker should open it — that is the keyboard flow —
 * while mouse users click when they mean it, so a focus that merely follows a
 * pointer must not open anything. Module-level because modality is global.
 */
let keyboardModality = false
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', () => { keyboardModality = true }, true)
  window.addEventListener('pointerdown', () => { keyboardModality = false }, true)
}
export function lastInputWasKeyboard(): boolean {
  return keyboardModality
}

/**
 * A compact searchable picker: a chip-height trigger opening a popover with a
 * filter input and a keyboard-navigable list. Single or multi select.
 *
 * The keyboard contract (built for driving the whole builder without a mouse):
 *
 *   focus (via Tab or `/`)  opens the popover, cursor in the search input
 *   type                    filters the options
 *   ↑ / ↓                   move the highlight (focus stays in the input)
 *   Space                   toggles the highlighted option — typing still
 *                           filters, because no stream or field name contains
 *                           a space, so Space is free to mean "select"
 *   Enter / OK button       COMMIT: keep the selection, close
 *   Escape                  REVERT: restore the selection as it was when the
 *                           popover opened, close
 *   /                       returns focus to the search input from anywhere
 *                           inside the popover
 *
 * Commit/revert exists because multi-select needs an "I'm done" that is
 * distinct from "select this one" — without it, closing the popover was the
 * only confirmation and a stray Escape silently kept half a selection.
 *
 * Optional extras:
 *   onCreate     a "Create "<typed>"" row under the list while the filter has
 *                text. Pressing it commits, closes, and hands over the trimmed
 *                text — what "create" means (a form, a request) is the caller's.
 *   optionColor  a color dot before each option it returns a value for.
 */
type PickGroup = { id: 'selected' | 'rest'; items: { id: string }[] }

export function PickList({
  label,
  options,
  selected,
  onChange,
  multi = false,
  placeholder = 'pick…',
  disabled = false,
  accent = false,
  onCreate,
  createLabel = (q) => `Create "${q}"`,
  optionColor,
}: {
  label: string
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  multi?: boolean
  placeholder?: string
  disabled?: boolean
  /** Louder trigger styling for the primary control (the stream picker). */
  accent?: boolean
  /** Offer to create the typed filter text (trimmed) as a new option. */
  onCreate?: (query: string) => void
  /** The create row's label for the typed text. */
  createLabel?: (query: string) => string
  /** A CSS color for an option's dot; '' or undefined draws none. */
  optionColor?: (option: string) => string | undefined
}) {
  const [open, setOpen] = useState(false)
  /** The filter text, for the create row. */
  const [query, setQuery] = useState('')
  const createText = query.trim()
  const inputRef = useRef<HTMLInputElement>(null)
  /** Selection at open time — what Escape restores. */
  const atOpen = useRef<string[]>([])
  /**
   * Closing restores focus to the trigger, and that focus is a keyboard-
   * modality focus — without this guard the auto-open would immediately
   * reopen the popover that was just closed. Cleared when focus leaves.
   */
  const suppressAutoOpen = useRef(false)

  const summary =
    selected.length === 0
      ? placeholder
      : multi && selected.length > 1
        ? `${selected[0]} +${selected.length - 1}`
        : selected[0]

  const close = () => {
    suppressAutoOpen.current = true
    setOpen(false)
  }

  const handleOpenChange = (v: boolean) => {
    if (v) {
      atOpen.current = [...selected]
      setQuery('')
    } else suppressAutoOpen.current = true
    setOpen(v)
  }

  const dot = (option: string) => {
    const color = optionColor?.(option)
    return color ? <span className="pick-dot" aria-hidden="true" style={{ '--pick-dot': color } as CSSProperties} /> : null
  }

  /** The option the listbox currently highlights, via aria-activedescendant. */
  const focusedOption = (): string | null => {
    const ad = inputRef.current?.getAttribute('aria-activedescendant')
    const el = ad ? document.getElementById(ad) : null
    if (!el) return null
    const key = el.getAttribute('data-key') ?? el.textContent?.trim() ?? null
    return key !== null && options.includes(key) ? key : null
  }

  const toggle = (key: string) => {
    if (!multi) {
      onChange([key])
      close()
      return
    }
    onChange(selected.includes(key) ? selected.filter((s) => s !== key) : [...selected, key])
  }

  /**
   * Multi mode groups: selected first (in option order, not click order, so
   * the pinned block reads the same as the full list), then the rest. Empty
   * groups are omitted so the divider only appears when both sides exist.
   */
  const groups: PickGroup[] = multi
    ? (
        [
          { id: 'selected', items: options.filter((o) => selected.includes(o)).map((o) => ({ id: o })) },
          { id: 'rest', items: options.filter((o) => !selected.includes(o)).map((o) => ({ id: o })) },
        ] as PickGroup[]
      ).filter((g) => g.items.length > 0)
    : []

  const onSelection = (keys: Selection) => {
    const next = keys === 'all' ? options : [...keys].map(String)
    onChange(next)
    if (!multi) close()
  }

  return (
    <DialogTrigger isOpen={open} onOpenChange={handleOpenChange}>
      <Button
        className={`pick-btn${accent ? ' pick-btn-accent' : ''}`}
        aria-label={label}
        isDisabled={disabled}
        onFocus={() => {
          if (suppressAutoOpen.current || !lastInputWasKeyboard()) return
          handleOpenChange(true)
        }}
        onBlur={() => {
          suppressAutoOpen.current = false
        }}
        // `/` on the focused trigger reopens it. Focus alone cannot: after a
        // commit the trigger is already focused (so no focus event will ever
        // fire) and auto-open is suppressed to stop the close from undoing
        // itself. `/` is the explicit "open it" that cuts through both.
        onKeyDown={(e) => {
          if (e.key === '/') {
            e.preventDefault()
            suppressAutoOpen.current = false
            handleOpenChange(true)
          }
        }}
      >
        {summary}
        <span className="pick-caret" aria-hidden="true">▾</span>
      </Button>
      <Popover className="pick-pop" placement="bottom start">
        <Dialog className="pick-dialog" aria-label={label}>
          {/* RAC Dialog takes no key handler; a wrapper div owns the popover-
              wide keys. */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- key
              routing for focus management, not an interactive control */}
          <div
            className="pick-keys"
            onKeyDown={(e) => {
              // `/` from anywhere inside the popover returns to the search box.
              if (e.key === '/') {
                e.preventDefault()
                e.stopPropagation()
                inputRef.current?.focus()
                inputRef.current?.select()
              }
            }}
          >
          <Autocomplete
            filter={(textValue, inputValue) =>
              textValue.toLowerCase().includes(inputValue.trim().toLowerCase())
            }
          >
            <div className="pick-head">
              {/* eslint-disable-next-line jsx-a11y/no-autofocus -- focus MUST
                  move into a just-opened popover (that is the dialog pattern),
                  and the filter input is the reason the popover exists: the
                  interaction is open-type-Enter. */}
              <SearchField className="pick-search" aria-label={`Filter ${label}`} autoFocus onChange={setQuery}>
                <Input
                  ref={inputRef}
                  className="pick-input"
                  placeholder="type to search…"
                  onKeyDownCapture={(e) => {
                    // Owned keys are intercepted BEFORE the Autocomplete's own
                    // handlers: RAC wires Enter to "activate the highlighted
                    // item", but here Enter means commit-and-close, and Space
                    // takes over activation.
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      e.stopPropagation()
                      if (!multi) {
                        const key = focusedOption()
                        if (key) onChange([key])
                      }
                      close()
                    } else if (e.key === ' ') {
                      const key = focusedOption()
                      if (key) {
                        e.preventDefault()
                        e.stopPropagation()
                        toggle(key)
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      e.stopPropagation()
                      onChange(atOpen.current)
                      close()
                    }
                  }}
                />
              </SearchField>
              <Button
                className="pick-ok"
                aria-label={`Confirm ${label}`}
                onPress={close}
              >
                OK
              </Button>
            </div>
            {multi ? (
              // Selected options are pinned to the top, above a divider, so
              // the current selection is always visible — otherwise a default
              // that sorts far down the list scrolls out of view the moment
              // another option is picked and the user cannot tell what is
              // actually selected. Regrouping is live: toggling an option
              // moves it across the divider immediately.
              <ListBox
                className="pick-list"
                aria-label={label}
                selectionMode="multiple"
                selectedKeys={new Set(selected)}
                onSelectionChange={onSelection}
                items={groups}
              >
                {(group: PickGroup) => (
                  <ListBoxSection
                    className={`pick-group pick-group-${group.id}`}
                    id={group.id}
                    aria-label={group.id === 'selected' ? 'Selected' : 'Not selected'}
                    items={group.items}
                  >
                    {(item: { id: string }) => (
                      <ListBoxItem className="pick-item" id={item.id} textValue={item.id}>
                        <span className="pick-mark" aria-hidden="true" />
                        {dot(item.id)}
                        {item.id}
                      </ListBoxItem>
                    )}
                  </ListBoxSection>
                )}
              </ListBox>
            ) : (
              <ListBox
                className="pick-list"
                aria-label={label}
                selectionMode="single"
                selectedKeys={new Set(selected)}
                onSelectionChange={onSelection}
                items={options.map((o) => ({ id: o }))}
              >
                {(item: { id: string }) => (
                  <ListBoxItem className="pick-item" id={item.id} textValue={item.id}>
                    {dot(item.id)}
                    {item.id}
                  </ListBoxItem>
                )}
              </ListBox>
            )}
          </Autocomplete>
          {onCreate && createText ? (
            <Button
              className="pick-create"
              aria-label={createLabel(createText)}
              onPress={() => {
                close()
                onCreate(createText)
              }}
            >
              <span aria-hidden="true">+</span> {createLabel(createText)}
            </Button>
          ) : null}
          {multi ? (
            <span className="pick-hint" aria-hidden="true">
              ↑↓ move · space select · ⏎ ok · esc cancel
            </span>
          ) : null}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}
